import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PERMISSIONS } from '@lims/permissions';
import { ALL_QUOTATION_STATUSES } from '@lims/shared-types';
import { quotationsApi } from '../../api/endpoints.js';
import type { QuotationSummary } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import {
  ActionButton,
  DataTable,
  FilterBar,
  PageHeader,
  Panel,
  SearchField,
  StatusBadge,
  SwitchField,
  type DataColumn,
} from '../../components/ui/index.js';
import { formatDate, formatMoney } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import { RecordNumber, ViewTabs } from '../clients/record-layout.js';

/**
 * The quotations list.
 *
 * THE STATUS COLUMN RENDERS `effectiveStatus`, NOT `status`. Expiry is derived
 * by the backend from the validity date against the laboratory's own calendar
 * day; recomputing it here would be a second implementation of the rule, free
 * to disagree with the first. The interface renders the fact it is given.
 *
 * A quotation for somebody with no client file is shown by NAME, marked as a
 * prospective client rather than left with a blank client column.
 *
 * DELETED QUOTATIONS (D210) are left out. A holder of `quotations.delete` may
 * show them — marked Deleted — to check what was removed (D214); the server
 * refuses the request to anybody else, so the switch is not the protection.
 */
export function QuotationsPage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const navigate = useNavigate();
  const locale = currentLocale();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [showDeleted, setShowDeleted] = useState(false);
  const canSeeDeleted = can(PERMISSIONS.QUOTATIONS_DELETE);
  const includeDeleted = canSeeDeleted && showDeleted;

  const quotations = useQuery({
    queryKey: ['quotations', { page, search, status, includeDeleted }],
    queryFn: () =>
      quotationsApi.list({
        page,
        search,
        ...(includeDeleted ? { includeDeleted: true } : {}),
        // `expired` is the DERIVED state, so it filters on the date rather
        // than on the stored column — which is why it is a separate parameter.
        ...(status === 'expired'
          ? { expired: true }
          : status
            ? { status }
            : {}),
      }),
  });

  const toneFor = (effective: string) => {
    if (effective === 'accepted') return 'success' as const;
    if (effective === 'rejected' || effective === 'expired') {
      return 'warning' as const;
    }
    return 'neutral' as const;
  };

  const columns: readonly DataColumn<QuotationSummary>[] = [
    {
      key: 'number',
      header: t('quotations.number'),
      render: (quotation) => (
        // The number opens the quotation: the row's way in (D278).
        <RecordNumber to={`/quotations/${quotation.id}`}>
          {quotation.quotationNumber}
        </RecordNumber>
      ),
    },
    {
      key: 'client',
      header: t('quotations.client'),
      render: (quotation) => (
        <div className="min-w-0">
          <span className="block truncate text-default-800" dir="auto">
            {quotation.party.name ??
              t('common.notRecorded')}
          </span>
          {quotation.party.kind === 'prospect' ? (
            <span className="text-caption text-default-600">
              {t('quotations.prospect')}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'status',
      header: t('quotations.statusLabel'),
      render: (quotation) => (
        <div className="flex flex-wrap items-center gap-1">
          {/* The DERIVED state. Never recomputed here. */}
          <StatusBadge
            label={t(`status.quotation.${quotation.effectiveStatus}`)}
            tone={toneFor(quotation.effectiveStatus)}
          />
          {quotation.isDeleted ? (
            <StatusBadge label={t('quotations.deleted.chip')} tone="danger" />
          ) : null}
        </div>
      ),
    },
    {
      key: 'validUntil',
      header: t('quotations.validUntil'),
      render: (quotation) => (
        <span className="whitespace-nowrap text-default-700">
          {formatDate(quotation.validUntil, { locale })}
        </span>
      ),
    },
    {
      key: 'total',
      header: t('quotations.total'),
      align: 'end',
      render: (quotation) => (
        <span className="measure text-default-900" dir="ltr">
          {formatMoney(quotation.totalMinor, {
            locale,
            currency: quotation.currency,
          })}
        </span>
      ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      isHeaderHidden: true,
      align: 'end',
      render: (quotation) => (
        <ActionButton
          priority="tertiary"
          size="sm"
          onPress={() => navigate(`/quotations/${quotation.id}`)}
        >
          {t('common.open')}
        </ActionButton>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('quotations.title')}
        subtitle={t('quotations.subtitle')}
        breadcrumbs={[{ label: t('nav.quotations') }]}
        actions={
          can(PERMISSIONS.QUOTATIONS_CREATE) ? (
            <ActionButton
              priority="primary"
              onPress={() => navigate('/quotations/new')}
            >
              {t('quotations.create')}
            </ActionButton>
          ) : null
        }
      />

      <ViewTabs
        label={t('quotations.statusLabel')}
        value={status}
        onChange={(value) => {
          setStatus(value);
          setPage(1);
        }}
        options={[
          { value: '', label: t('common.all') },
          ...ALL_QUOTATION_STATUSES.map((value) => ({
            value,
            label: t(`status.quotation.${value}`),
          })),
        ]}
      />

      <Panel>
        <FilterBar>
          <SearchField
            value={search}
            onChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
            placeholder={t('quotations.searchPlaceholder')}
          />
          {canSeeDeleted ? (
            <SwitchField
              label={t('quotations.deleted.show')}
              value={showDeleted}
              onChange={(value) => {
                setShowDeleted(value);
                setPage(1);
              }}
            />
          ) : null}
        </FilterBar>

        <DataTable
          label={t('quotations.title')}
          columns={columns}
          rows={quotations.data?.data ?? []}
          rowKey={(quotation) => quotation.id}
          isLoading={quotations.isLoading}
          error={quotations.error}
          onRetry={() => void quotations.refetch()}
          emptyContent={
            search ? t('quotations.noneFound') : t('quotations.noneYet')
          }
          page={page}
          totalPages={quotations.data?.meta.totalPages ?? 1}
          onPageChange={setPage}
        />
      </Panel>
    </>
  );
}
